import { HEALTH_TRAIT_BODY_FUNCTION_FIELDS } from 'constants/query/healthTraitBodyFunctionsQuery';
import { HEALTH_TRAIT_RESULT_TYPE_FIELDS_QUERY } from 'constants/query/healthTraitResultTypeQuery';
import { SECTION_BULLET_LIST_QUERY } from 'constants/query/sections/sectionBulletListQuery';
import {
  getSectionHealthTraitResultFieldsQuery,
} from 'constants/query/sections/sectionHealthTraitResultFieldsQuery';
import { SECTION_OPEN_URL_QUERY } from 'constants/query/sections/sectionOpenUrlQuery';
import { getSectionTextIconQuery } from 'constants/query/sections/sectionTextIconQuery';
import {
  getSectionTraitDescriptionQuery,
} from 'constants/query/sections/sectionTraitDescriptionQuery';
import { getImageWithAltFragment, Pagination } from 'petdna-lib-uicomponents/hygraph';
import { HealthTraitInfoFilters } from 'stores/types/Traits';

import { SECTION_HEALTH_TRAIT_AFFECTED_BREEDS_QUERY } from './sections/sectionHealthTraitAffectedBreedsQuery';
import { SECTION_TEXT_AND_TITLE_QUERY } from './sections/sectionTextAndTitleQuery';

export const getHealthTraitInfoListQueryForFilters = (
  filters: HealthTraitInfoFilters,
  pagination: Pagination,
) => {
  const args = [
    filters.traitIds
      ? { variable: '$traitIds: [String]', filter: 'traitId_in: $traitIds' }
      : null,
    filters.breedIds
      ? { variable: '$breedIds: [String]', filter: 'affectedBreeds_some: { breedID_in: $breedIds }' }
      : null,
    filters.bodyFunctionIds
      ? { variable: '$bodyFunctionIds: [ID]', filter: 'bodyFunction: { id_in: $bodyFunctionIds }' }
      : null,
    filters.query
      ? { variable: '$query: String', filter: 'title_contains: $query' }
      : null,
  ].filter(Boolean) as { variable: string, filter: string }[];
  const params = args.length
    ? `(where: {${args.map((arg) => arg.filter).join(', ')}}, skip: ${pagination.skip}, first: ${pagination.first})`
    : `(skip: ${pagination.skip}, first: ${pagination.first})`;

  return `
    query HealthTraitsQuery${args.length ? `(${args.map((arg) => arg.variable).join(', ')})` : ''} {
      healthTraits${params} {
        traitId
        inheritance
        title
        titleAbbreviation
        description
        bodyFunction {
          ${HEALTH_TRAIT_BODY_FUNCTION_FIELDS}
        }
      }
    }
  `;
};

export const getHealthTraitsForInheritance = (pagination: Pagination) => `
  query HealthTraitsQuery {
    healthTraits(skip: ${pagination.skip}, first: ${pagination.first}) {
      traitId
      inheritance
      affectedBreeds {
        breedID
      }
    }
  }
`;

export const getHealthTraitInheritanceResults = (pagination: Pagination) => `
  query HealthTraitInheritanceResultsQuery {
    healthTraitInheritanceResults(skip: ${pagination.skip}, first: ${pagination.first}) {
      inheritance
      gender
      hasMatchingBreeds
      predictionType {
        ${HEALTH_TRAIT_RESULT_TYPE_FIELDS_QUERY}
      }
      displayType {
        ${HEALTH_TRAIT_RESULT_TYPE_FIELDS_QUERY}
      }
      title {
        html
      }
      description {
        html
      }
    }
  }
`;

export const HEALTH_TRAIT_INFO_QUERY = `
  query HealthTraitsQuery($traitId: String!, $countryCode: [SaleCountry!], $locale: [Locale!]! = [en]) {
    healthTraits(where: { traitId_in: [$traitId] }, locales: $locale) {
      traitId
      title
      description
      inheritance
      bodyFunction {
        ${HEALTH_TRAIT_BODY_FUNCTION_FIELDS}
      }
      genesTitle {
        html
      }
      genes
      affectedBreeds(first: 100) {
        name
      }
      healthTraitPartnerships(where: {OR: [{countryCode_contains_some: $countryCode}, {countryCode_contains_some: US}]}) {
        countryCode
        partnership {
          name
          partnerLogo {
            ${getImageWithAltFragment({})}
          }
          redirectType
          healthTraitsUrl
        }
      }
      sectionsHealthTraitResults {
        ${getSectionHealthTraitResultFieldsQuery()}
      }
      sections {
        __typename
        ... on ${getSectionTraitDescriptionQuery(400, 400)}
        ... on ${SECTION_TEXT_AND_TITLE_QUERY}
        ... on ${SECTION_BULLET_LIST_QUERY}
        ... on ${SECTION_OPEN_URL_QUERY}
        ... on ${SECTION_HEALTH_TRAIT_AFFECTED_BREEDS_QUERY}
        ... on ${getSectionTextIconQuery()}
      }
    }
  }
`;

export const getHealthTraitInfoListQueryPDFData = (pagination: Pagination) => {
  const params = `(skip: ${pagination.skip}, first: ${pagination.first})`;

  return `query HealthTraitInfoListQueryPDFData {
      healthTraits${params} {
        traitId
        inheritance
        title
        genes
        geneVariant
        affectedBreeds {
          breedID
        }
        bodyFunction {
          title
          icon {
            url
          }
        }
      }
    }
  `;
};
